import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Menu,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  deleteSafetyQuestion,
  fetchSafetyQuestions,
} from "../../services/safetyAPI";
import CreateSafetyQuestionModal from "./CreateSafetyQuestionModal";
import UpdateSafetyQuestionModal from "./UpdateSafetyQuestionModal";
import CopySafetyQuestionsFromProjectModal from "./CopySafetyQuestionsFromProjectModal";

/** Gear modal: manage project safety questions (Create / Copy / Update / Delete). */
const SafetyQuestionsModal = ({ visible, onClose }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const queryClient = useQueryClient();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["safetyQuestions", projectId],
    queryFn: () => fetchSafetyQuestions(projectId),
    enabled: visible && !!projectId,
  });

  const questions = data?.questions || [];

  useEffect(() => {
    if (!visible) {
      setIsCreateModalVisible(false);
      setIsUpdateModalVisible(false);
      setIsCopyModalVisible(false);
      setSelectedQuestion(null);
    }
  }, [visible]);

  const deleteMutation = useMutation({
    mutationFn: deleteSafetyQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["safetyQuestions", projectId],
      });
      notification.success({ message: "Safety question deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete safety question",
        description:
          error.response?.data?.detail || error.message || "Unknown error",
      });
    },
  });

  const columns = [
    {
      title: "#",
      width: 64,
      render: (_value, _record, index) => index + 1,
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 220,
      render: (value) => value || "—",
    },
    {
      title: "Question",
      dataIndex: "text",
    },
    {
      title: "Actions",
      width: 80,
      render: (_value, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="1"
                onClick={() => {
                  setSelectedQuestion(record);
                  setIsUpdateModalVisible(true);
                }}
              >
                Update
              </Menu.Item>
              <Menu.Item key="2">
                <Popconfirm
                  title="Are you sure you want to delete this safety question?"
                  onConfirm={() => deleteMutation.mutate(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  Delete
                </Popconfirm>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Safety Questions"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={960}
        destroyOnClose
        centered
      >
        <div className="flex gap-4 mb-4 flex-wrap">
          <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
            Create
          </Button>
          <Button type="default" onClick={() => setIsCopyModalVisible(true)}>
            Copy from Project
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={questions}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Modal>

      <CreateSafetyQuestionModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      {selectedQuestion && (
        <UpdateSafetyQuestionModal
          visible={isUpdateModalVisible}
          onClose={() => {
            setIsUpdateModalVisible(false);
            setSelectedQuestion(null);
          }}
          question={selectedQuestion}
        />
      )}
      <CopySafetyQuestionsFromProjectModal
        visible={isCopyModalVisible}
        onClose={() => setIsCopyModalVisible(false)}
      />
    </>
  );
};

export default SafetyQuestionsModal;
